import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import driverRouteService from '../../services/driverRouteService';
import { getFriendlyErrorMessage } from '../../utils/errorHandler';
import { mapDriverRouteForView } from './roteiroMappers';

const DriverRouteContext = createContext(null);

/** Cache em memória por roteiro — sobrevive a remontagens do Outlet. */
const routeCache = new Map();

function readCache(routeId) {
    if (routeId == null || routeId === '') return null;
    return routeCache.get(String(routeId)) ?? null;
}

function writeCache(routeId, apiRoute, roteiro) {
    if (routeId == null || routeId === '') return;
    routeCache.set(String(routeId), { apiRoute, roteiro });
}

export function DriverRouteProvider() {
    const { id } = useParams();
    const routeId = id != null ? String(id) : '';
    const cached = readCache(routeId);

    const [apiRoute, setApiRoute] = useState(cached?.apiRoute ?? null);
    const [roteiro, setRoteiro] = useState(cached?.roteiro ?? null);
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState(null);
    const loadedRouteIdRef = useRef(cached ? routeId : null);

    const loadRoute = useCallback(async ({ showLoading = true, force = false } = {}) => {
        if (!routeId) return;

        if (!force && loadedRouteIdRef.current === routeId && readCache(routeId)) {
            const hit = readCache(routeId);
            setApiRoute(hit.apiRoute);
            setRoteiro(hit.roteiro);
            setError(null);
            if (showLoading) {
                setLoading(false);
            }
            return;
        }

        if (showLoading) {
            setLoading(true);
        }
        setError(null);

        try {
            const routeData = await driverRouteService.get(routeId);
            const mapped = mapDriverRouteForView(routeData);
            writeCache(routeId, routeData, mapped);
            loadedRouteIdRef.current = routeId;
            setApiRoute(routeData);
            setRoteiro(mapped);
        } catch (err) {
            routeCache.delete(routeId);
            loadedRouteIdRef.current = null;
            setApiRoute(null);
            setRoteiro(null);
            setError(getFriendlyErrorMessage(err) || 'Não foi possível carregar o roteiro.');
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [routeId]);

    useEffect(() => {
        loadRoute({ showLoading: !readCache(routeId) });
    }, [loadRoute, routeId]);

    const refreshRoute = useCallback(async () => {
        await loadRoute({ showLoading: false, force: true });
    }, [loadRoute]);

    const value = useMemo(
        () => ({
            routeId,
            apiRoute,
            roteiro,
            loading,
            error,
            refreshRoute,
            reloadRoute: () => loadRoute({ showLoading: true, force: true }),
        }),
        [routeId, apiRoute, roteiro, loading, error, refreshRoute, loadRoute],
    );

    return (
        <DriverRouteContext.Provider value={value}>
            <Outlet />
        </DriverRouteContext.Provider>
    );
}

export function useDriverRoute() {
    const context = useContext(DriverRouteContext);
    if (!context) {
        throw new Error('useDriverRoute deve ser usado dentro de DriverRouteProvider.');
    }
    return context;
}
